import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import { ASC } from 'app/shared/util/pagination.constants';
import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IComment, defaultValue } from 'app/shared/model/comment.model';

const initialState: EntityState<IComment> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  updateSuccess: false,
};

const apiUrl = 'api/comments';

// Actions

export const getEntities = createAsyncThunk(
  'comment/fetch_entity_list',
  async ({ page, size, sort, datasetId }: IQueryParams & { datasetId: string | number }) => {
    const requestUrl = `${apiUrl}?${sort ? `sort=${sort}&` : ''}datasetId=${datasetId}&cacheBuster=${new Date().getTime()}`;
    return axios.get<IComment[]>(requestUrl);
  },
);

export const getEntity = createAsyncThunk(
  'comment/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IComment>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const createEntity = createAsyncThunk(
  'comment/create_entity',
  async (entity: IComment, thunkAPI) => {
    const result = await axios.post<IComment>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({ datasetId: entity.dataset.id }));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateEntity = createAsyncThunk(
  'comment/update_entity',
  async (entity: IComment, thunkAPI) => {
    const result = await axios.put<IComment>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({ datasetId: entity.dataset.id }));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const partialUpdateEntity = createAsyncThunk(
  'comment/partial_update_entity',
  async (entity: IComment, thunkAPI) => {
    const result = await axios.patch<IComment>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({ datasetId: entity.dataset.id }));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const deleteEntity = createAsyncThunk(
  'comment/delete_entity',
  async ({ id, datasetId }: { id: string | number; datasetId: number }, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IComment>(requestUrl);
    thunkAPI.dispatch(getEntities({ datasetId }));
    return result;
  },
  { serializeError: serializeAxiosError },
);

// slice

export const CommentSlice = createEntitySlice({
  name: 'comment',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const { data } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data.sort((a, b) => {
            if (!action.meta?.arg?.sort) {
              return 1;
            }
            const order = action.meta.arg.sort.split(',')[1];
            const predicate = action.meta.arg.sort.split(',')[0];
            return order === ASC ? (a[predicate] < b[predicate] ? -1 : 1) : b[predicate] < a[predicate] ? -1 : 1;
          }),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = CommentSlice.actions;

// Reducer
export default CommentSlice.reducer;
